/**
 * Bastion chart series palette — one color per financial family, the same on
 * every stock-page chart (factor chapters, Verdict boards, X-Ray, Financials
 * table, brief cards, share images).
 *
 * Two jobs, two color sets:
 *   - Data series use this cool range (violet → periwinkle → sky → cyan, plus
 *     fuchsia for cash) and neutrals. Neighbouring hues read as one family on
 *     the dark board.
 *   - Warm / traffic-light colors are reserved for meaning, never for series:
 *     green / yellow / red rating tiers and signs (up / down, cash vs debt,
 *     revenue growth), orange risk. Keeping them out of the data is what stops
 *     a yellow GEAR+ radar competing with a yellow EBIT line.
 *
 * A metric takes its family's color wherever it appears: EBITDA is violet in
 * Profits, Margins and EV/EBITDA; P/E and EPS are sky because both are built
 * on earnings; FCF Yield is fuchsia like FCF.
 */

const VIOLET = '#b197fc';
const PERIWINKLE = '#7aa2f7';
const SKY = '#38bdf8';
const CYAN = '#22d3ee';
const INDIGO = '#818cf8';
const FUCHSIA = '#e879f9';

export const CHART_COLORS = {
  /** Revenue family — the neutral hero: Revenue, EV/Sales. */
  revenue: '#d6d3d1',
  /** EBITDA family: EBITDA, EBITDA margin, EV/EBITDA. */
  ebitda: VIOLET,
  /** EBIT family: EBIT, EBIT margin. */
  ebit: PERIWINKLE,
  /** Earnings family: Net income, net margin, EPS, P/E. */
  earnings: SKY,
  /** Free cash flow family: FCF, FCF margin, FCF yield. */
  fcf: FUCHSIA,
  /** Operating cash flow (and cash-flow margins), buybacks. */
  operatingCash: INDIGO,
  /** Enterprise value (EV & Market Cap chart). */
  enterprise: INDIGO,
  /** Market family: price, market cap, price / total return lines, shares. */
  market: CYAN,
  /** Shareholder income: dividends, DPS, dividend yield. */
  dividends: CYAN,
  /** Capex — neutral, it is context for cash flow. */
  capex: '#d4d4d8',
  /** Debt charts: total debt (and Total Debt / EBITDA). */
  debt: VIOLET,
  /** Debt charts: net debt (and Net Debt / EBITDA). */
  debtNet: PERIWINKLE,
} as const;

export type ChartColorFamily = keyof typeof CHART_COLORS;
